import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCalendar, FiLoader, FiRefreshCcw } from "react-icons/fi";
import { YEAR } from "../EDADashboard/utils";

import { DEFAULT_YEAR, formatSegmentTitle, isTotalRow, formatTableCellValue } from "./utils";
import { useCashInSegment } from "./useCashInSegment";
import TableBadge from "./components/TableBadge";

const EDACashInSegmentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    dashboard, selectedProcess, segment, segmentKey,
    periodOptions, selectedPeriod, setSelectedPeriod,
    selectedYear, setSelectedYear,
    loading, error,
    rows, monthColumns, groupRows, groupMonthColumns, stateRows, stateMonthColumns,
    selectedCategory, handleRowClick,
    classificationLoading, classificationError, classificationData,
    transformedClassificationData, classificationMonths, segmentColumns
  } = useCashInSegment(location);

  const segmentTitle = formatSegmentTitle(segment);

  if (!location.state || !selectedPeriod) {
    return (
      <div className="space-y-5 animate-pulse">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="h-8 w-80 rounded bg-slate-200"></div>
          <div className="mt-3 h-4 w-48 rounded bg-slate-200"></div>
          <div className="mt-6 flex gap-4">
            <div className="h-10 w-32 rounded-lg bg-slate-200"></div>
            <div className="h-10 w-48 rounded-lg bg-slate-200"></div>
            <div className="h-10 w-24 rounded-lg bg-slate-200"></div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="mb-4 h-8 rounded bg-slate-100" />
          ))}
        </div>
      </div>
    );
  }

  const renderSummaryTable = (title, rowsData, columnsData, badgeLabel, badgeColor) => {
    let rowCounter = 0;
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          {badgeLabel && <TableBadge label={badgeLabel} color={badgeColor} />}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-slate-700">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Category</th>
                {columnsData.map((month) => (
                  <th key={month.key} className="px-4 py-3 text-right">{month.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowsData.map((row, index) => {
                const isTotal = isTotalRow(row.Category);
                if (!isTotal) rowCounter += 1;
                const displayIndex = isTotal ? "" : rowCounter;
                return (
                  <tr key={`${row.Category}-${index}`} className={`border-t ${isTotal ? "bg-slate-100 font-semibold text-slate-800" : "hover:bg-slate-50"}`}>
                    <td className="px-4 py-3 text-slate-500">{displayIndex}</td>
                    <td className="px-4 py-3">{row.Category}</td>
                    {columnsData.map((month) => (
                      <td key={month.key} className="px-4 py-3 text-right font-mono">
                        {formatTableCellValue(row[month.key], !isTotal)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{segmentTitle} - Cash In</h1>
            <p className="mt-1 text-sm text-slate-500">Process: {selectedProcess} | Status: {dashboard?.edaStatus || "Available"}</p>
          </div>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800">
            <FiArrowLeft className="h-4 w-4" /> Back
          </button>
        </div>

        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-400">Year</span>
              <select value={selectedYear} onChange={(event) => setSelectedYear(Number(event.target.value))} disabled={loading} className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50">
                {YEAR.map((year) => <option key={year.value} value={year.value}>{year.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-400">Period</span>
              <select value={selectedPeriod?.value || ""} onChange={(e) => setSelectedPeriod(periodOptions.find((option) => option.value === e.target.value))} disabled={loading || !periodOptions.length} className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50">
                {periodOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => { if (periodOptions.length > 0) { setSelectedPeriod(periodOptions[0]); } setSelectedYear(DEFAULT_YEAR); }} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50">
              <FiRefreshCcw className="h-3.5 w-3.5" /> Reset
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {loading && (
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                <FiLoader className="h-4 w-4 animate-spin" /> Loading summary...
              </div>
            )}
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
              <FiCalendar className="h-4 w-4" /> Current period: {selectedPeriod.label} of {selectedYear}
            </div>
          </div>
        </div>
      </div>

      {segmentKey === "service" ? (
        <>
          {renderSummaryTable("Group-wise Collection Summary", groupRows, groupMonthColumns, "Group-wise", "green")}
          {renderSummaryTable("State-wise Collection Summary", stateRows, stateMonthColumns, "State-wise", "green")}
        </>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4">
            <div>
              <p className="text-sm font-medium text-slate-400">Home / Insights / EDA Dashboard Detail / {segmentTitle} - Cash In</p>
              <h2 className="mt-2 text-lg font-semibold text-slate-800">Cash In Category Wise - Monthly Actuals (Rs Cr)</h2>
            </div>
            <TableBadge label={segmentTitle} color={segmentKey === "onshore" ? "blue" : "slate"} />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-slate-700">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">Category</th>
                  {monthColumns.map((month) => <th key={month.key} className="px-4 py-3 text-right">{month.title}</th>)}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let counter = 0;
                  return rows.map((row, index) => {
                    const isTotal = isTotalRow(row.Category);
                    if (!isTotal) counter += 1;
                    const displayIndex = isTotal ? "" : counter;
                    return (
                      <tr key={index} className={`border-t ${isTotal ? "bg-slate-100 font-semibold text-slate-800" : "hover:bg-slate-50 cursor-pointer"}`} onClick={() => handleRowClick(row.Category)}>
                        <td className="px-4 py-3 text-slate-500">{displayIndex}</td>
                        <td className="px-4 py-3">{row.Category}</td>
                        {monthColumns.map((month) => (
                          <td key={month.key} className="px-4 py-3 text-right font-mono">{formatTableCellValue(row[month.key], !isTotal)}</td>
                        ))}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedCategory && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <h2 className="text-lg font-semibold text-slate-800">Cost Classification for: {selectedCategory}</h2>
          </div>
          {classificationLoading && (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-slate-500">
              <FiLoader className="h-5 w-5 animate-spin" /><span>Loading classification details...</span>
            </div>
          )}
          {classificationError && (
            <div className="m-4 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{classificationError}</div>
          )}
          {!classificationLoading && !classificationError && (
            <div className="overflow-x-auto">
              {classificationData.length > 0 ? (
                <table className="min-w-full table-fixed border-collapse text-sm text-slate-700">
                  <thead className="bg-slate-50">
                    <tr>
                      <th rowSpan={2} className="w-16 text-center px-4 py-3">#</th>
                      <th rowSpan={2} className="w-80 px-4 py-3 text-left">Particulars</th>
                      {classificationMonths.map((month) => (
                        <th key={month} colSpan={segmentColumns.length} className="text-center font-bold text-blue-600">{month}</th>
                      ))}
                    </tr>
                    <tr>
                      {classificationMonths.flatMap((month) => segmentColumns.map((col) => (
                        <th key={`${month}-${col}`} className="text-center font-semibold">{col}</th>
                      )))}
                    </tr>
                  </thead>
                  <tbody>
                    {transformedClassificationData.map((row, index) => {
                      const isTotal = row.Particulars.toLowerCase() === "total";
                      return (
                        <tr key={index} className={isTotal ? "bg-slate-50 font-bold" : "border-t"}>
                          <td className="px-4 py-3">{isTotal ? "" : index + 1}</td>
                          <td className="px-4 py-3">{row.Particulars}</td>
                          {classificationMonths.map((month) => segmentColumns.map((col) => {
                            const value = row[`${month}_${col}`];
                            return (
                              <td key={`${month}-${col}`} className={`px-4 py-3 text-center font-mono ${value > 0 ? "text-green-600" : value < 0 ? "text-red-600" : "text-slate-400"}`}>
                                {formatTableCellValue(value)}
                              </td>
                            );
                          }))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="py-8 text-center text-sm text-slate-400">No classification data available for {selectedCategory}.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EDACashInSegmentDetail;
